import axios from 'axios';
import { AuthService } from 'services/auth/authService';
import { TemplateStopType } from '../enums/TemplateStopType';

const baseUri = `${process.env.REACT_APP_API_URI}templateStop`;

export class TemplateStopService {
  //
  // template stop functions
  //

  static async templateStopCreation(date, shipperName) {
    let newDate = new Date(date);
    let dateIsoString = newDate.toISOString();
    const reponse = await axios.get(
      `${baseUri}/templateStop/creationService?date=${dateIsoString}&shipperName=${shipperName}`,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return reponse.data;
  }

  static async getTemplateStopById(templateStopId) {
    const response = await axios.get(
      `${baseUri}/templateStop/${templateStopId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async getTemplateStopsWithFilter(filter) {
    const response = await axios.get(`${baseUri}/templateStop/byFilter`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
      params: filter,
    });
    return response.data;
  }

  static async deleteTemplateStopById(templateStopId) {
    return await axios.delete(`${baseUri}/templateStop/${templateStopId}`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
    });
  }

  static async updateTemplateStop(templateStop, templateStopId) {
    const response = await axios.put(
      `${baseUri}/templateStop/${templateStopId}`,
      templateStop,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async updatePickupDeliveryRelations(
    templateStop,
    oldRelatedTemplateStops,
    newRelatedTemplateStops
  ) {
    const deletedRelations = oldRelatedTemplateStops.filter(
      (oldRelatedTemplateStop) =>
        !newRelatedTemplateStops.some(
          (newRelatedTemplateStop) =>
            oldRelatedTemplateStop.id === newRelatedTemplateStop.id
        )
    );
    const addedRelations = newRelatedTemplateStops.filter(
      (newRelatedTemplateStop) =>
        !oldRelatedTemplateStops.some(
          (oldRelatedTemplateStop) =>
            newRelatedTemplateStop.id === oldRelatedTemplateStop.id
        )
    );
    await axios.put(
      `${baseUri}/templateStop/deleteRelations`,
      deletedRelations.map((deletedRelation) =>
        templateStop.stopType === TemplateStopType.Pickup
          ? {
            pickupStopId: templateStop.id,
            deliveryStopId: deletedRelation.id,
          }
          : {
            pickupStopId: deletedRelation.id,
            deliveryStopId: templateStop.id,
          }
      ),
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    await axios.post(
      `${baseUri}/templateStop/relations`,
      addedRelations.map((addedRelation) =>
        templateStop.stopType === TemplateStopType.Pickup
          ? {
            pickupStopId: templateStop.id,
            deliveryStopId: addedRelation.id,
          }
          : { pickupStopId: addedRelation.id, deliveryStopId: templateStop.id }
      ),
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
  }

  static async createTemplateStop(templateStop) {
    const response = await axios.post(`${baseUri}/templateStop`, templateStop, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
    });
    return response.data;
  }

  //
  // template stop instance functions
  //

  static async getTemplateStopInstanceById(templateStopInstanceId) {
    const response = await axios.get(
      `${baseUri}/templateStopInstance/${templateStopInstanceId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async getTemplateStopInstancesWithFilter(filter) {
    const response = await axios.get(
      `${baseUri}/templateStopInstance/byFilter`,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
        params: filter,
      }
    );
    return response.data;
  }

  static async deleteTemplateStopInstanceById(templateStopInstanceId) {
    return await axios.delete(
      `${baseUri}/templateStopInstance/${templateStopInstanceId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
  }

  static async getTemplateStopInstancesWithoutTourWithFilter(filter) {
    const response = await axios.get(
      `${baseUri}/templateStopInstance/withoutTour`,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
        params: filter,
      }
    );
    return response.data;
  }

  //
  // template functions
  //

  static async createTemplate(template, filter) {
    const response = await axios.post(`${baseUri}/template`, template, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
      params: filter,
    });
    return response.data;
  }

  static async updateTemplate(template) {
    const response = await axios.put(`${baseUri}/template/`, template, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
    });
    return response.data;
  }

  static async duplicateTemplate(templateId, filter) {
    const response = await axios.post(
      `${baseUri}/template/${templateId}/duplicate`,
      filter,
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async deleteTemplate(templateId) {
    return await axios.delete(`${baseUri}/template/${templateId}`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
    });
  }

  static async getTemplatesWithStopsAndDurationsByFilter(filter) {
    const response = await axios.get(`${baseUri}/template/byFilter`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
      params: filter,
    });
    return response.data;
  }

  static async reorderTemplates(templateIds, filter) {
    const response = await axios.put(
      `${baseUri}/template/reorderTemplates`,
      { templateIds },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
        params: filter,
      }
    );
    return response.data;
  }

  static async moveTemplateStopToTemplate(
    templateStopId,
    sourceTemplateId,
    targetTemplateId
  ) {
    const response = await axios.put(
      `${baseUri}/template/moveStop`,
      { templateStopId, sourceTemplateId, targetTemplateId },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async reorderTemplateStops(templateId, templateStopIds) {
    const response = await axios.put(
      `${baseUri}/template/reorderStops`,
      { templateId, templateStopIds },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async addTemplateStopsToTemplate(templateId, templateStopIds) {
    const response = await axios.post(
      `${baseUri}/template/addStops`,
      { templateId, templateStopIds },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async getTemplatePreviewDuration(templateId, templateStopIds) {
    const response = await axios.put(
      `${baseUri}/template/previewDuration`,
      { templateId, templateStopIds },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async deleteTemplateStopFromTemplate(templateId, templateStopId) {
    const response = await axios.put(
      `${baseUri}/template/deleteStop`,
      { templateId, templateStopId },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
      }
    );
    return response.data;
  }

  static async optimizeTemplates(optimizeTemplateRequest) {
    try {
      var response = await axios.put(
        `${baseUri}/template/optimizeTemplates`,
        optimizeTemplateRequest,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: AuthService.getAuthHeader(),
          },
        }
      );
    } catch (error) {
      console.error('Error optimizing templates:', error);
    }
    return response.data;
  }

  static async getJobStatus(jobIdentifier) {
    const response = await axios.get(
        `${baseUri}/template/job-status/${jobIdentifier}`,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: AuthService.getAuthHeader(),
          },
        }
    );
    try {
    } catch (error) {
      console.error('Error getting optimized templates:', error);
    }
    return response.data;
  }
  
  static async getOptimizationResult(jobIdentifier, filter) {
    const response = await axios.get(
        `${baseUri}/template/template-results/${jobIdentifier}`,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: AuthService.getAuthHeader(),
          },
          params: filter,
        }
    );
    try {
    } catch (error) {
      console.error('Error getting optimized templates:', error);
    }
    return response.data;
  }

  static async importTemplates(templates, filter) {
    const response = await axios.post(
      `${baseUri}/template/import`,
      { templates },
      {
        headers: {
          'Content-Type': 'application/json',
          authorization: AuthService.getAuthHeader(),
        },
        params: filter,
      }
    );
    return response.data;
  }

  //
  // tour functions
  //

  static async getTourById(tourId) {
    const response = await axios.get(`${baseUri}/tour/${tourId}`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
    });
    return response.data;
  }

  static async getToursWithFilter(filter) {
    const response = await axios.get(`${baseUri}/tour/byFilter`, {
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
      params: filter,
    });
    return response.data;
  }

  static async getStopCategoriesAsync(shipperName) {
    const response = await axios.get(`${baseUri}/templateStop/stop-categories`, {
      params: {shipperName},
      headers: {
        'Content-Type': 'application/json',
        authorization: AuthService.getAuthHeader(),
      },
    });
    return response.data;
  }
  
  static async RecalculateTemplatesDistanceAndDuration(templateIds) {
    const response = await axios.post(
        `${baseUri}/template/recalculate-distance-and-duration`,
        templateIds,
        {
          headers: {
            'Content-Type': 'application/json',
            authorization: AuthService.getAuthHeader(),
          }
        }
    );
    return response.data;
  }
}
